import { createStyles, Theme, makeStyles, Box, Container } from '@material-ui/core';
import { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import './TopSplitter.css';
import { AssetsDashboardContext } from '../contexts/assetsDashboardContext';
import { switchPointPercentage } from '../pages/AssetsDashboard/AssetsDashboard';
import MapOverviewContainer from '../containers/MapOverviewContainer';
import { LEFT_PANEL_WIDTH } from '../utils/utilityHelper';
import TableView from '../pages/AssetsDashboard/TableView';
import { useRouteMatch } from 'react-router-dom';
import PagePage from './Page/PagePage';
import VisibilityModal from './Map/component/ControlsOverlay/VisibilityButton/Visibility';
import MapSearchControlOverlay from './Map/component/ControlsOverlay/MapSearchControlOverlay';

const ANIMATION_TIME_IN_SECONDS = 0.3;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomSection: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    topRightSection: {
      display: 'flex',
      flex: '1 1 auto',
      maxWidth: '100%',
    },
    tableView: {
      height: '100%',
      '& .splitter-layout': {
        width: `calc(100% - ${LEFT_PANEL_WIDTH}px)`,
      },
    },
    splitter2: {
      '& > .layout-pane:nth-child(1)': {
        transition: `width ${ANIMATION_TIME_IN_SECONDS}s`,
      },
    },
    mapRoot: {
      padding: 0,
      width: '100%',
      height: '100%',
      maxWidth: '100%',
    },
  })
);

export interface TopSplitterProps {
  i?: Array<any>;
  inProp: boolean;
  onExited?: () => void;
  fullScreen?: boolean;
  height?: any;
  width: number;
  setWidth: any;
  onDragEnd?: (verticalPercentage: number) => void;
  minimizeAndExpandTable?: (value: number) => void;
  isMobileView?: boolean;
  isMapView?: boolean;
  isVertical: boolean;
}

const TopSplitter: FunctionComponent<TopSplitterProps> = props => {
  const { height, width, onDragEnd, minimizeAndExpandTable, isVertical } = props;
  const classes = useStyles();
  const {
    verticalPercentage,
    setVerticalPercentage,
    fullscreen,
    isMobileView,
    visibilityModal,
    setVisibilityModal,
    currentPage,
    mapFabOpen,
  } = useContext(AssetsDashboardContext);
  const splitterLayoutRef = useRef<SplitterLayout | null>(null);
  const [page, setPage] = useState<string | undefined>();

  const m1 = useRouteMatch({
    path: `/:type?/mainpage/:page/:id?`,
  });

  const m2 = useRouteMatch({
    path: `/mainpage/:page/:id?`,
  });

  const match: { params: { page?: string; type?: string; id?: string } } = useMemo(
    () => m1 || m2 || { params: {} },
    [m1, m2]
  );

  useEffect(() => {
    setPage(prev => (prev === match.params.page ? prev : match.params.page));
  }, [match]);

  const handleDragEnd = () => {
    if (verticalPercentage >= switchPointPercentage) {
      splitterLayoutRef.current && splitterLayoutRef.current.setState({ secondaryPaneSize: 100 });
    }
    onDragEnd && onDragEnd(verticalPercentage!);
  };
  const BoxContainer = isMobileView ? Box : Container;
  return (
    <div className={fullscreen ? classes.tableView : classes.topRightSection}>
      {!fullscreen ? (
        <BoxContainer className={classes.mapRoot}>
          {page ? (
            <div
              style={{
                maxWidth: isVertical
                  ? undefined
                  : props.isMobileView
                  ? `100vw`
                  : `calc(${100 - verticalPercentage}vw - 70px)`,
                maxHeight: !isVertical
                  ? '100vh'
                  : !props.isMobileView
                  ? `${100 - verticalPercentage}vh`
                  : 'calc(100vh - 64px)',
                overflowY: 'scroll',
                marginLeft: 12,
                marginRight: 12,
              }}>
              <div style={{ height: 60, marginBottom: 12, borderBottom: '2px solid #f0f0f0' }}>
                <MapSearchControlOverlay
                  setVisibilityModal={setVisibilityModal}
                  isModalOpen={visibilityModal}
                  hideVisibility={false}
                  setBounds={() => {}}
                  hideSearch={true}
                  mapContainerRef={null}
                  style={{ marginTop: 10 }}
                />

                <VisibilityModal
                  setModalOpen={setVisibilityModal}
                  modalOpen={visibilityModal}
                  mapContainerRef={null}
                  isHidden={mapFabOpen}
                  height={props.isMobileView ? 'calc(100vh - 64px)' : `calc(${100 - verticalPercentage}vh - 46px)`}
                />
              </div>
              <PagePage page={page} id={match.params.id} noMargin showLoader></PagePage>
            </div>
          ) : (
            <MapOverviewContainer height={height} width={width} />
          )}
        </BoxContainer>
      ) : (
        <SplitterLayout
          ref={splitterLayoutRef}
          percentage
          secondaryInitialSize={props.isMobileView ? 0 : fullscreen ? (isVertical ? 99.45 : 99.75) : verticalPercentage}
          vertical={isVertical}
          customClassName="custom-splitter"
          onSecondaryPaneSizeChange={setVerticalPercentage}
          onDragEnd={handleDragEnd}>
          <div />
          {!props.isMobileView && (
            <TableView
              {...{
                isMobileView: false,
                isVertical,
                height: (verticalPercentage / 100) * window.innerHeight,
                minimizeAndExpandTable,
                classes,
              }}
            />
          )}
        </SplitterLayout>
      )}
    </div>
  );
};

export default TopSplitter;
